import { useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import {
  ApiError,
  NetworkError,
  NOTICE_ROLE_LABEL,
  flagApi,
  type FlagLookup,
  type FlagResult,
  type FlagSummary,
  type NoticeRole,
} from '../lib/api';

/**
 * "Is this vendor at <site>?" — the page behind the link in a property notice.
 *
 * A photo of a QR sign works from anywhere, so a sign-in proves a vendor has
 * the sign's code, not that they are at the gate. When a vendor signs in, the
 * property is emailed, and the email's one action is this page: someone who
 * knows the vendor isn't there flags the sign-in. The flag is marked on the
 * vendor log under the recipient the notice went to, and the senior property
 * manager is emailed when one is listed.
 *
 * PUBLIC, like the vendor flow. The flag belongs to whoever the notice was
 * sent to — a manager, an assistant, or the site's shared inbox — so the link
 * itself is the credential: the notice id and an HMAC over it, in the URL
 * FRAGMENT. A browser never sends a fragment anywhere, so the token stays out
 * of the access logs, App Insights' request URLs, proxies and the Referer.
 * This page reads it once, takes it out of the address bar, and sends it in a
 * POST body (lib/api.ts, flagApi).
 *
 * Opening the page changes nothing. Mail scanners open links, and some run the
 * page's script; only pressing the button records a flag.
 *
 * This page is in the vendor bundle: nothing here may import MSAL or anything
 * from staff/.
 */

/**
 * "#<notice id>.<mac>": the notice's UUID, then 43 base64url characters of
 * HMAC. The id is lowercased because the MAC is taken over the lowercase form
 * and a UUID's case means nothing.
 */
const LINK_RE =
  /^#([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.([A-Za-z0-9_-]{43})$/i;

interface Link {
  noticeId: string;
  mac: string;
}

function readLink(hash: string): Link | null {
  const m = LINK_RE.exec(hash.trim());
  if (!m || !m[1] || !m[2]) return null;
  return { noticeId: m[1].toLowerCase(), mac: m[2] };
}

/**
 * A fragment is there but is not a link: mangled in transit, most likely —
 * worth telling ops. No fragment at all is not: this page strips its own on
 * load, so every reload of /flag arrives without one.
 */
function isMangled(hash: string): boolean {
  return hash.replace(/^#/, '').trim() !== '' && readLink(hash) === null;
}

interface Notice {
  tone: 'err' | 'warn';
  icon: 'circle-alert' | 'triangle-alert';
  text: string;
}

type View =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'expired' }
  /** Worth another try from this page: the link is still in memory. */
  | { kind: 'retry'; notice: Notice }
  | { kind: 'ready'; data: FlagLookup }
  | { kind: 'flagged'; data: FlagLookup; line: string };

/**
 * A failed call, as this page answers it. 'invalid' and 'expired' are final
 * for this link; anything else can be tried again from the page, which keeps
 * the link in memory. A reload cannot: the fragment has left the address bar.
 */
function failedView(err: unknown): Extract<View, { kind: 'invalid' | 'expired' | 'retry' }> {
  if (err instanceof ApiError) {
    switch (err.code) {
      // A wrong MAC, no such notice, or a visit the retention purge has
      // removed: one answer, as the API gives one answer.
      case 'invalid_link':
      case 'not_found':
        return { kind: 'invalid' };
      case 'expired':
        return { kind: 'expired' };
      case 'rate_limited':
        return {
          kind: 'retry',
          notice: {
            tone: 'warn',
            icon: 'triangle-alert',
            text: 'Too many tries — wait a minute and try again.',
          },
        };
      default:
        break;
    }
  }
  if (err instanceof NetworkError) {
    return {
      kind: 'retry',
      notice: {
        tone: 'warn',
        icon: 'triangle-alert',
        text: "We couldn't reach the network. Check your signal and try again.",
      },
    };
  }
  return {
    kind: 'retry',
    notice: { tone: 'err', icon: 'circle-alert', text: 'Something went wrong. Please try again.' },
  };
}

/** Who flagged, inside a sentence. The site inbox is nobody in particular. */
function flagger(f: { byName: string | null; byRole: NoticeRole }): string {
  if (f.byName) return f.byName;
  return f.byRole === 'site_inbox'
    ? 'the site inbox'
    : `the ${NOTICE_ROLE_LABEL[f.byRole].toLowerCase()}`;
}

function alreadyFlaggedLine(f: FlagSummary): string {
  return f.byYou
    ? `You flagged this at ${f.atLocal}.`
    : `Already flagged by ${flagger(f)} at ${f.atLocal}.`;
}

/** "A", "A and B", "A, B and C". */
function joinNames(names: string[]): string {
  if (names.length < 2) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** What pressing the button did, in one sentence that is true for this answer. */
function resultLine(r: FlagResult): string {
  const { attempted, notified, failed } = r.escalation;
  // THIS request's escalation first, whatever else is true: the request that
  // emailed the senior property manager — or failed to — always says so,
  // and a failure always asks for a call.
  if (attempted) {
    if (notified.length > 0) return `It's on the vendor log, and we emailed ${joinNames(notified)}.`;
    if (failed) {
      return "It's on the vendor log, but we couldn't email your senior property manager — please call them.";
    }
    // The flagger is the only senior property manager listed.
    return "It's on the vendor log.";
  }
  if (r.status === 'already_flagged') return alreadyFlaggedLine(r.flag);
  // Another recipient's flag won the visit, and carried the email to the
  // senior property manager — "nobody is listed" would be false here.
  if (!r.flag.byYou) {
    return `It's on the vendor log, and ${flagger(r.flag)} had already flagged it at ${r.flag.atLocal}.`;
  }
  // This was the visit's first flag, and the site lists no senior property manager.
  return "It's on the vendor log. No senior property manager is listed for this site, so nobody else was emailed.";
}

/**
 * How the visit ended, as the status row says it. Only a vendor's own
 * sign-out is "Signed out": the evening sweep and a staff close record when
 * we stopped counting, not when the vendor left.
 */
function closedLine(visit: FlagLookup['visit']): string {
  const at = visit.signedOutAtLocal ? ` ${visit.signedOutAtLocal}` : '';
  switch (visit.closedReason) {
    case 'vendor':
      return `Signed out${at}`;
    case 'auto_end_of_day':
      return `Auto-closed${at} — no sign-out was recorded`;
    case 'staff':
      return `Closed by staff${at}`;
    default:
      return `Closed${at}`;
  }
}

// ───────────────────────────────────────────────────────────────── chrome

/** The notice email's own header: "Vendor Check-In" over the site's name. */
function Header({ site }: { site: FlagLookup['site'] | null }): JSX.Element {
  return (
    <div className="vs-hdr">
      <div className="vs-hdr-row">
        <div>
          <div className="vs-eyebrow">Vendor Check-In</div>
        </div>
        <img className="vs-logo" src="/assets/logo-white.png" alt="US Storage Centers" />
      </div>
      {site && (
        <div className="vs-site">
          <div className="vs-site-name">{site.name}</div>
          {site.label && <div className="vs-site-meta">{site.label}</div>}
        </div>
      )}
    </div>
  );
}

function NoticeBox({ notice }: { notice: Notice }): JSX.Element {
  return (
    <div className={`vs-notice ${notice.tone}`} role="alert">
      <Icon name={notice.icon} size={16} />
      <span>{notice.text}</span>
    </div>
  );
}

function VisitCard({ visit }: { visit: FlagLookup['visit'] }): JSX.Element {
  const rows: [string, string][] = [
    ['Name', visit.name],
    ['Company', visit.company],
    ['Purpose', visit.purpose],
    ['Signed in', `${visit.signedInAtLocal} · ${visit.dateLine}`],
  ];
  return (
    <div className="vs-card">
      {rows.map(([k, v]) => (
        <div className="vs-kv" key={k}>
          <span className="k">{k}</span>
          <span className="v">{v}</span>
        </div>
      ))}
      <div className="vs-kv">
        <span className="k">Status</span>
        <span className="v">
          {visit.onSite ? (
            <span className="vs-badge on">
              <Icon name="circle-check" size={11} />
              On site
            </span>
          ) : (
            // Plain text, not a pill: "Auto-closed … — no sign-out was
            // recorded" is a sentence, and a phone has to wrap it.
            closedLine(visit)
          )}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────── page

export default function FlagSignIn(): JSX.Element {
  // Read once. In development StrictMode runs this initializer twice, both
  // times before the effect below clears the fragment, so both see the link.
  const [link] = useState<Link | null>(() => readLink(window.location.hash));
  const [mangled] = useState<boolean>(() => isMangled(window.location.hash));
  const reported = useRef(false);
  const [view, setView] = useState<View>(() =>
    link ? { kind: 'loading' } : { kind: 'invalid' },
  );
  const [attempt, setAttempt] = useState(0);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  // busy lands a render late: two taps inside one frame would both see false.
  const flagging = useRef(false);

  // Out of the address bar, and so out of history, a bookmark, a screenshot
  // and a tab left open on a phone. A reload then finds no link and says the
  // link isn't valid; the email still has it. The router's own history state
  // is handed back unchanged.
  useEffect(() => {
    if (!window.location.hash) return;
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search,
    );
  }, []);

  // Tell ops a link arrived unreadable — without any of it. Once: StrictMode
  // keeps refs across its remount in development.
  useEffect(() => {
    if (!mangled || reported.current) return;
    reported.current = true;
    flagApi.reportMalformed().catch(() => undefined);
  }, [mangled]);

  useEffect(() => {
    if (!link) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await flagApi.lookup(link.noticeId, link.mac);
        if (cancelled) return;
        setView(
          data.flag
            ? { kind: 'flagged', data, line: alreadyFlaggedLine(data.flag) }
            : { kind: 'ready', data },
        );
      } catch (err) {
        if (!cancelled) setView(failedView(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [link, attempt]);

  const retry = (): void => {
    setView({ kind: 'loading' });
    setAttempt((n) => n + 1);
  };

  const submit = async (): Promise<void> => {
    if (!link || flagging.current) return;
    flagging.current = true;
    setBusy(true);
    setNotice(null);
    try {
      const result = await flagApi.flag(link.noticeId, link.mac);
      setView((v) =>
        v.kind === 'ready' ? { kind: 'flagged', data: v.data, line: resultLine(result) } : v,
      );
    } catch (err) {
      // A link that stopped working between opening and pressing (the visit
      // purged, the link's days passed) leaves the page; anything else stays
      // on it, with the button, to try again. A failed flag wrote nothing
      // (both writes commit together), so the next tap is a first flag.
      const failed = failedView(err);
      if (failed.kind === 'retry') setNotice(failed.notice);
      else setView(failed);
    } finally {
      flagging.current = false;
      setBusy(false);
    }
  };

  if (view.kind === 'loading') {
    return (
      <div className="vs-plain">
        <div className="vs-spinner" style={{ color: 'var(--us-blue)' }} />
      </div>
    );
  }

  if (view.kind === 'invalid' || view.kind === 'expired') {
    return (
      <div className="vs">
        <Header site={null} />
        <div className="vs-body">
          {view.kind === 'invalid' ? (
            <>
              <h1 className="vs-h1">This link isn't valid</h1>
              <p className="vs-p">Use the link in your most recent email.</p>
            </>
          ) : (
            <>
              <h1 className="vs-h1">This link has expired</h1>
              {/* No number: each link's lifetime was fixed when its notice was
                  queued, and the email it came in says until when. */}
              <p className="vs-p">Flag links stop working a set time after the sign-in — the email says when.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (view.kind === 'retry') {
    return (
      <div className="vs">
        <Header site={null} />
        <div className="vs-body">
          <h1 className="vs-h1">We couldn't open this sign-in</h1>
          <NoticeBox notice={view.notice} />
          <div className="vs-foot">
            <button className="vs-btn secondary" onClick={retry}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view.kind === 'flagged') {
    return (
      <div className="vs">
        <Header site={view.data.site} />
        <div className="vs-body">
          <div className="vs-hero">
            <div className="ring flag">
              <Icon name="flag" size={32} />
            </div>
            <h2>Flagged.</h2>
            <p>{view.line}</p>
          </div>
          <VisitCard visit={view.data.visit} />
        </div>
      </div>
    );
  }

  const { data } = view;
  return (
    <div className="vs">
      <Header site={data.site} />
      <div className="vs-body">
        <h1 className="vs-h1">Is this vendor at {data.site.name}?</h1>
        <p className="vs-p">
          If they aren't at the property, flag this sign-in. It's marked on the vendor log{' '}
          {data.recipient.role === 'site_inbox' ? 'under the site inbox' : 'under your name'}
          {/* Promised only when a flag from this link would email one right now. */}
          {data.seniorListed ? ', and your senior property manager is emailed.' : '.'}
        </p>
        {notice && <NoticeBox notice={notice} />}
        <VisitCard visit={data.visit} />
        <div className="vs-foot">
          <button
            className="vs-btn danger"
            onClick={() => void submit()}
            disabled={busy}
            data-busy={busy}
          >
            {busy ? <span className="vs-spinner" /> : <Icon name="flag" size={17} />}
            Flag this sign-in
          </button>
          <p className="vs-legal">Only flag a vendor who isn't here.</p>
        </div>
      </div>
    </div>
  );
}
